import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';
import {
  TRAVEL_GUIDE_DEFAULTS,
  TRAVEL_GUIDE_SETTING_KEY,
  travelGuideFromSettings,
  serializeTravelGuide,
} from '../../config/travelGuide';

const newSection = () => ({
  id: `section-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  title: 'New section',
  body: '',
});

const TravelGuideManagement = () => {
  const [guide, setGuide] = useState(TRAVEL_GUIDE_DEFAULTS);
  const [moduleVisible, setModuleVisible] = useState('1');
  const [activeGuideId, setActiveGuideId] = useState(TRAVEL_GUIDE_DEFAULTS.guides[0].id);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.GET_SETTINGS);
      if (res.data.success) {
        const s = res.data.settings || {};
        setGuide(travelGuideFromSettings(s));
        setModuleVisible(s.module_travel_guide_visible ?? '1');
        const parsed = travelGuideFromSettings(s);
        if (parsed.guides[0]) setActiveGuideId(parsed.guides[0].id);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const activeGuide = guide.guides.find((g) => g.id === activeGuideId) || guide.guides[0];

  const updateGuideField = (guideId, field, value) => {
    setGuide((prev) => ({
      ...prev,
      guides: prev.guides.map((g) => (g.id === guideId ? { ...g, [field]: value } : g)),
    }));
  };

  const updateSection = (guideId, sectionId, field, value) => {
    setGuide((prev) => ({
      ...prev,
      guides: prev.guides.map((g) => {
        if (g.id !== guideId) return g;
        return {
          ...g,
          sections: g.sections.map((s) =>
            s.id === sectionId ? { ...s, [field]: value } : s
          ),
        };
      }),
    }));
  };

  const addSection = (guideId) => {
    setGuide((prev) => ({
      ...prev,
      guides: prev.guides.map((g) =>
        g.id === guideId ? { ...g, sections: [...g.sections, newSection()] } : g
      ),
    }));
  };

  const removeSection = (guideId, sectionId) => {
    if (!window.confirm('Remove this section?')) return;
    setGuide((prev) => ({
      ...prev,
      guides: prev.guides.map((g) =>
        g.id === guideId
          ? { ...g, sections: g.sections.filter((s) => s.id !== sectionId) }
          : g
      ),
    }));
  };

  const moveSection = (guideId, index, dir) => {
    setGuide((prev) => ({
      ...prev,
      guides: prev.guides.map((g) => {
        if (g.id !== guideId) return g;
        const next = [...g.sections];
        const target = index + dir;
        if (target < 0 || target >= next.length) return g;
        [next[index], next[target]] = [next[target], next[index]];
        return { ...g, sections: next };
      }),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(API_ENDPOINTS.ADMIN_SETTINGS, {
        settings: {
          [TRAVEL_GUIDE_SETTING_KEY]: serializeTravelGuide(guide),
          module_travel_guide_visible: moduleVisible,
        },
      });
      alert('Travel guide saved.');
    } catch (e) {
      alert('Error saving travel guide');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = () => {
    if (!window.confirm('Replace all travel guide content with the original PDF defaults?')) {
      return;
    }
    setGuide(JSON.parse(JSON.stringify(TRAVEL_GUIDE_DEFAULTS)));
    setActiveGuideId(TRAVEL_GUIDE_DEFAULTS.guides[0].id);
  };

  if (loading) return <div className="loading">Loading travel guide…</div>;
  if (!activeGuide) return <div className="loading">No travel options configured.</div>;

  return (
    <div className="settings travel-guide-admin">
      <p className="settings-section-hint">
        Edit the public <strong>Travel guide</strong> tab: page headers and the three travel
        options (Fly, Bus, Self Drive). Each option has dropdown sections guests can expand.
      </p>

      <div className="form-group">
        <label className="module-toggle-label">
          <input
            type="checkbox"
            checked={moduleVisible === '1'}
            onChange={(e) => setModuleVisible(e.target.checked ? '1' : '0')}
          />{' '}
          Show Travel guide on the public site
        </label>
      </div>

      <h2 className="settings-section-title">Page headers</h2>
      <div className="form-group">
        <label>Modal title</label>
        <input
          type="text"
          value={guide.page_title}
          onChange={(e) => setGuide({ ...guide, page_title: e.target.value })}
        />
      </div>
      <div className="form-group">
        <label>Subtitle</label>
        <input
          type="text"
          value={guide.page_subtitle}
          onChange={(e) => setGuide({ ...guide, page_subtitle: e.target.value })}
        />
      </div>
      <div className="form-group">
        <label>Closing note</label>
        <textarea
          rows={2}
          value={guide.closing_note}
          onChange={(e) => setGuide({ ...guide, closing_note: e.target.value })}
        />
      </div>

      <h2 className="settings-section-title">Travel options</h2>
      <div className="travel-admin-tabs">
        {guide.guides.map((g) => (
          <button
            key={g.id}
            type="button"
            className={`btn-outline-small${g.id === activeGuide.id ? ' is-active' : ''}`}
            onClick={() => setActiveGuideId(g.id)}
          >
            {g.title}
          </button>
        ))}
      </div>

      <div className="travel-admin-panel">
        <div className="form-group">
          <label>Option title</label>
          <input
            type="text"
            value={activeGuide.title}
            onChange={(e) => updateGuideField(activeGuide.id, 'title', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Short description (on the tab card)</label>
          <input
            type="text"
            value={activeGuide.short_description}
            onChange={(e) =>
              updateGuideField(activeGuide.id, 'short_description', e.target.value)
            }
          />
        </div>
        <div className="form-group">
          <label>Intro paragraph</label>
          <textarea
            rows={3}
            value={activeGuide.intro}
            onChange={(e) => updateGuideField(activeGuide.id, 'intro', e.target.value)}
          />
        </div>

        <h3 className="settings-subsection-title">Dropdown sections</h3>
        <p className="form-hint">
          Guests tap a section header to expand the details. Use blank lines between paragraphs.
          Paste full URLs (https://…) and they become clickable links on the public site.
        </p>

        {activeGuide.sections.map((section, index) => (
          <div key={section.id} className="travel-admin-section site-card">
            <div className="travel-admin-section-toolbar">
              <strong>Section {index + 1}</strong>
              <div className="travel-admin-section-actions">
                <button
                  type="button"
                  className="btn-icon"
                  title="Move up"
                  onClick={() => moveSection(activeGuide.id, index, -1)}
                  disabled={index === 0}
                >
                  <ChevronUp size={16} />
                </button>
                <button
                  type="button"
                  className="btn-icon"
                  title="Move down"
                  onClick={() => moveSection(activeGuide.id, index, 1)}
                  disabled={index === activeGuide.sections.length - 1}
                >
                  <ChevronDown size={16} />
                </button>
                <button
                  type="button"
                  className="btn-icon danger"
                  title="Remove"
                  onClick={() => removeSection(activeGuide.id, section.id)}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            <div className="form-group">
              <label>Section header</label>
              <input
                type="text"
                value={section.title}
                onChange={(e) =>
                  updateSection(activeGuide.id, section.id, 'title', e.target.value)
                }
              />
            </div>
            <div className="form-group">
              <label>Section content</label>
              <textarea
                rows={6}
                value={section.body}
                onChange={(e) =>
                  updateSection(activeGuide.id, section.id, 'body', e.target.value)
                }
              />
            </div>
          </div>
        ))}

        <button
          type="button"
          className="btn-secondary"
          onClick={() => addSection(activeGuide.id)}
        >
          <Plus size={16} /> Add section
        </button>
      </div>

      <div className="form-actions" style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Travel Guide'}
        </button>
        <button type="button" className="btn-secondary" onClick={handleResetDefaults}>
          Reset to PDF defaults
        </button>
      </div>
    </div>
  );
};

export default TravelGuideManagement;
